// Shared helpers for the email composers (new / reply / reply-all / forward)

// ── Quoted / forwarded content handling ──────────────────────────────────
// The original email (when replying/forwarding) is kept as raw HTML OUTSIDE the
// TipTap editor (TipTap's schema strips tables/styles/images). We wrap it in a
// sentinel div so drafts can be split back into "user message" + "quote".
const QUOTE_OPEN = '<div data-mf-quote="1">';
const QUOTE_CLOSE = '</div><!--/mf-quote-->';

export function wrapQuote(quoteHtml: string): string {
  if (!quoteHtml) return '';
  return QUOTE_OPEN + quoteHtml + QUOTE_CLOSE;
}

// Split a stored body back into the editable user part and the quoted part.
export function splitQuote(html: string): { userHtml: string; quoteHtml: string } {
  if (!html) return { userHtml: '', quoteHtml: '' };
  const start = html.indexOf(QUOTE_OPEN);
  if (start === -1) return { userHtml: html, quoteHtml: '' };
  const userHtml = html.slice(0, start);
  let quoteHtml = html.slice(start + QUOTE_OPEN.length);
  const end = quoteHtml.indexOf(QUOTE_CLOSE);
  if (end !== -1) quoteHtml = quoteHtml.slice(0, end);
  return { userHtml, quoteHtml };
}

// Client-side stable id generator (used for draft ids and attachment keys)
export function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
      return (crypto as any).randomUUID();
    }
  } catch {}
  return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Insert an image node directly via the ProseMirror view (no TipTap editor ref needed)
function insertImageNode(view: any, dataUrl: string) {
  try {
    const { state } = view;
    const imageType = state.schema.nodes.image;
    if (!imageType) return;
    const node = imageType.create({ src: dataUrl });
    const tr = state.tr.replaceSelectionWith(node);
    view.dispatch(tr);
  } catch {
    /* ignore */
  }
}

/**
 * Build TipTap `editorProps` paste/drop handlers that:
 *  - insert pasted/dropped IMAGES inline (as base64) into the editor
 *  - add pasted/dropped non-image FILES to the attachment list
 * Works for both Ctrl/Cmd+V and the browser's native right-click "Paste"
 * (the OS context menu is auto-localized, so all languages are supported).
 */
export function makePasteHandlers(addAttachment: (file: File) => void) {
  const processFiles = (view: any, files: File[]): boolean => {
    if (!files.length) return false;
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = ev => insertImageNode(view, ev.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        addAttachment(file);
      }
    });
    return true;
  };

  return {
    handlePaste: (view: any, event: ClipboardEvent) => {
      const files = event.clipboardData?.files;
      if (files && files.length > 0) {
        event.preventDefault();
        return processFiles(view, Array.from(files));
      }
      return false; // let ProseMirror handle text/html normally
    },
    handleDrop: (view: any, event: DragEvent) => {
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        event.preventDefault();
        return processFiles(view, Array.from(files));
      }
      return false;
    },
  };
}
